import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { SearchX, ScanSearch, Check, X as XIcon, ShieldAlert } from 'lucide-react';
import api, { errorMessage, toPage } from '../services/api';
import {
  PageHeader,
  Card,
  Segmented,
  SearchInput,
  Pagination,
  Sheet,
  Button,
  Badge,
  CopyButton,
  InfoRow,
  ErrorBanner,
  SkeletonList,
  EmptyState,
  ResponsiveList,
  inputClass,
  useDebounce,
  cx
} from '../components/ui';
import { formatCurrency, formatDate, formatDateTime, formatRelative, fromUnix } from '../utils/format';

const STATUS = {
  PENDING: { label: 'Chờ xử lý', tone: 'yellow' },
  SUCCESS: { label: 'Thành công', tone: 'green' },
  FAILED: { label: 'Từ chối', tone: 'red' }
};

const TYPE_LABEL = { 0: 'Mất đơn', 1: 'Ghi nhận gián tiếp' };

const PLATFORM_LABEL = { shopee: 'Shopee', tiktokshop: 'TikTok Shop', lazada: 'Lazada' };

// Tra cứu và tạo đơn tự động chỉ có ở Shopee; sàn khác đối chiếu tay trên báo cáo của sàn
const isShopee = (lo) => (lo.platform || 'shopee') === 'shopee';

const ORDER_EXISTS_REASON = 'Đơn hàng đã tồn tại trong hệ thống';

const REJECT_REASONS = [
  'Không tìm thấy đơn hàng',
  ORDER_EXISTS_REASON,
  'Dữ liệu không hợp lệ',
  'Đơn hàng không đủ điều kiện'
];

const isFraud = (item) => item.fraudStatus === 'FRAUD' || item.isFraud || item.is_fraud;

const LostOrdersPage = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState('PENDING');
  const [type, setType] = useState('');
  const [platform, setPlatform] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const debouncedSearch = useDebounce(search);

  // Kết quả tra cứu: mỗi phần tử gắn với ĐÚNG 1 yêu cầu lost order (lostOrder) để tạo/từ chối không nhầm user
  const [results, setResults] = useState(null); // [{ lostOrder, matched: {order_sn, shopee_data, matched_by} | null, reason }]
  const [selectedIds, setSelectedIds] = useState([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null); // mảng lost order id
  const [rejectReason, setRejectReason] = useState('');
  const [customReason, setCustomReason] = useState('');

  const { data, isLoading, isFetching, error, refetch } = useQuery(
    ['lost-orders', status, type, platform, debouncedSearch, page, limit],
    async () => {
      const params = { page, limit };
      if (status) params.status = status;
      if (type) params.type = type;
      if (platform) params.platform = platform;
      if (debouncedSearch) params.search = debouncedSearch;
      return toPage(await api.get('/admin/lost-order-claims', { params }));
    },
    { keepPreviousData: true }
  );

  const rows = data?.items || [];
  const pagination = data?.pagination || {};
  const pendingRows = rows.filter((r) => r.status === 'PENDING' && isShopee(r));

  const refreshAll = () => {
    queryClient.invalidateQueries('lost-orders');
    queryClient.invalidateQueries('nav-counts');
    queryClient.invalidateQueries('dashboard');
  };

  const lookup = async (lostOrders) => {
    if (!lostOrders.length) return;
    setSearching(true);
    try {
      const { data: res } = await api.post('/admin/lost-order-claims/lookup', {
        order_sns: [...new Set(lostOrders.map((l) => l.order_sn))]
      });
      const matchedBySn = new Map((res.data?.matched_orders || []).map((m) => [m.order_sn, m]));
      const unmatchedBySn = new Map((res.data?.unmatched_orders || []).map((u) => [u.order_sn, u]));
      const mapped = lostOrders.map((lo) => ({
        lostOrder: lo,
        matched: matchedBySn.get(lo.order_sn) || null,
        reason: unmatchedBySn.get(lo.order_sn)?.reason || null
      }));
      setResults(mapped);
      setSelectedIds(mapped.filter((r) => r.matched).map((r) => r.lostOrder.id));
    } catch (e) {
      toast.error(errorMessage(e, 'Lỗi khi tra cứu Shopee'));
    } finally {
      setSearching(false);
    }
  };

  const removeFromResults = (ids) => {
    setResults((prev) => {
      const next = (prev || []).filter((r) => !ids.includes(r.lostOrder.id));
      return next.length ? next : null;
    });
    setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
  };

  const createOrders = async (entries) => {
    setBusy(true);
    let ok = 0;
    let autoRejected = 0;
    const done = [];
    const errors = [];
    for (const entry of entries) {
      try {
        await api.post(`/admin/lost-order-claims/${entry.lostOrder.id}/resolve`, {
          order_sn: entry.matched.order_sn,
          shopee_data: entry.matched.shopee_data
        });
        ok += 1;
        done.push(entry.lostOrder.id);
      } catch (e) {
        const apiError = e?.response?.data?.error || {};
        const exists = apiError.code === 'ORDER_EXISTS' || /đã tồn tại/i.test(apiError.message || '');
        if (!exists) {
          errors.push(`${entry.lostOrder.order_sn}: ${apiError.message || 'lỗi'}`);
          continue;
        }
        // Đơn đã có trong hệ thống -> tự từ chối yêu cầu kèm lý do (user nhận thông báo)
        try {
          await api.post(`/admin/lost-order-claims/${entry.lostOrder.id}/reject`, { reason: ORDER_EXISTS_REASON });
          autoRejected += 1;
          done.push(entry.lostOrder.id);
        } catch (rejectErr) {
          errors.push(`${entry.lostOrder.order_sn}: đã tồn tại nhưng từ chối lỗi (${errorMessage(rejectErr, 'lỗi')})`);
        }
      }
    }
    removeFromResults(done);
    setBusy(false);
    refreshAll();
    const summary = [`Tạo ${ok} đơn`, autoRejected ? `tự từ chối ${autoRejected} đơn đã tồn tại` : null].filter(Boolean).join(', ');
    if (errors.length) toast.error(`${summary}, lỗi ${errors.length}\n${errors.slice(0, 3).join('\n')}`, { duration: 6000 });
    else toast.success(summary, { duration: autoRejected ? 5000 : 3000 });
  };

  const openReject = (ids) => {
    setRejectTarget(ids);
    setRejectReason('');
    setCustomReason('');
  };

  const confirmReject = async () => {
    const reason = (rejectReason === 'OTHER' ? customReason : rejectReason).trim();
    if (!reason) {
      toast.error('Chọn hoặc nhập lý do từ chối');
      return;
    }
    setBusy(true);
    let ok = 0;
    const done = [];
    for (const id of rejectTarget) {
      try {
        await api.post(`/admin/lost-order-claims/${id}/reject`, { reason });
        ok += 1;
        done.push(id);
      } catch (_) { /* đếm ở dưới */ }
    }
    removeFromResults(done);
    setBusy(false);
    setRejectTarget(null);
    refreshAll();
    if (ok < rejectTarget.length) toast.error(`Từ chối ${ok}/${rejectTarget.length} yêu cầu`);
    else toast.success(`Đã từ chối ${ok} yêu cầu`);
  };

  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const selectedEntries = (results || []).filter((r) => selectedIds.includes(r.lostOrder.id));
  const selectedMatched = selectedEntries.filter((r) => r.matched);

  const renderRow = (lo) => {
    const st = STATUS[lo.status] || { label: lo.status, tone: 'gray' };
    return (
      <Card key={lo.id} className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="flex items-center gap-1">
              <p className="truncate font-mono text-sm font-semibold text-gray-900">{lo.order_sn}</p>
              <CopyButton value={lo.order_sn} />
            </div>
            <p className="truncate text-xs text-gray-500">{lo.user?.name || '—'} · {lo.user?.email || '—'}</p>
          </div>
          <Badge tone={st.tone}>{st.label}</Badge>
        </div>
        <div className="mt-2 flex flex-wrap gap-1.5">
          <Badge tone="gray">{PLATFORM_LABEL[lo.platform] || 'Shopee'}</Badge>
          <Badge tone="blue">{TYPE_LABEL[lo.type] || lo.type}</Badge>
        </div>
        {lo.reason && <p className="mt-2 text-xs text-gray-600">Mô tả: {lo.reason}</p>}
        {lo.failed_reason && <p className="mt-1 text-xs text-red-600">Lý do từ chối: {lo.failed_reason}</p>}
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-100 pt-3">
          <span className="text-xs text-gray-400">Gửi {formatRelative(lo.created_at)}</span>
          {lo.status === 'PENDING' && (
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="text-red-600" onClick={() => openReject([lo.id])} disabled={busy}>
                <XIcon className="h-4 w-4" />
              </Button>
              {isShopee(lo) ? (
                <Button size="sm" onClick={() => lookup([lo])} loading={searching} disabled={busy}>
                  <ScanSearch className="h-4 w-4" /> Tra cứu
                </Button>
              ) : (
                <span className="self-center text-xs text-gray-500">Đối chiếu tay</span>
              )}
            </div>
          )}
        </div>
      </Card>
    );
  };

  const columns = [
    {
      key: 'sn',
      title: 'Mã đơn',
      render: (lo) => (
        <span className="inline-flex items-center gap-1">
          <span className="font-mono font-medium text-gray-900">{lo.order_sn}</span>
          <CopyButton value={lo.order_sn} />
        </span>
      )
    },
    {
      key: 'user',
      title: 'Người dùng',
      render: (lo) => (
        <div>
          <p className="font-medium text-gray-900">{lo.user?.name || '—'}</p>
          <p className="text-xs text-gray-500">{lo.user?.email || '—'}</p>
        </div>
      )
    },
    { key: 'platform', title: 'Sàn', render: (lo) => <Badge tone="gray">{PLATFORM_LABEL[lo.platform] || 'Shopee'}</Badge> },
    { key: 'type', title: 'Loại', render: (lo) => <Badge tone="blue">{TYPE_LABEL[lo.type] || lo.type}</Badge> },
    {
      key: 'reason',
      title: 'Mô tả',
      tdClass: 'max-w-xs',
      render: (lo) => (
        <div className="text-xs">
          {lo.reason && <p className="line-clamp-2 text-gray-600">{lo.reason}</p>}
          {lo.failed_reason && <p className="line-clamp-2 text-red-600">Từ chối: {lo.failed_reason}</p>}
          {!lo.reason && !lo.failed_reason && <span className="text-gray-400">—</span>}
        </div>
      )
    },
    { key: 'status', title: 'Trạng thái', render: (lo) => { const st = STATUS[lo.status] || { label: lo.status, tone: 'gray' }; return <Badge tone={st.tone}>{st.label}</Badge>; } },
    { key: 'created', title: 'Gửi lúc', render: (lo) => <span className="whitespace-nowrap text-xs text-gray-500">{formatRelative(lo.created_at)}</span> },
    {
      key: 'actions',
      title: '',
      align: 'right',
      render: (lo) => (lo.status === 'PENDING' ? (
        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" size="sm" className="text-red-600" onClick={() => openReject([lo.id])} disabled={busy} aria-label={`Từ chối ${lo.order_sn}`}>
            <XIcon className="h-4 w-4" />
          </Button>
          {isShopee(lo) ? (
            <Button size="sm" onClick={() => lookup([lo])} loading={searching} disabled={busy}>
              <ScanSearch className="h-4 w-4" /> Tra cứu
            </Button>
          ) : (
            <span className="whitespace-nowrap text-xs text-gray-500">Đối chiếu tay</span>
          )}
        </div>
      ) : null)
    }
  ];

  const renderResult = (entry) => {
    const { lostOrder: lo, matched } = entry;
    const sd = matched?.shopee_data || {};
    const items = Array.isArray(sd.items) ? sd.items : [];
    const commission = items.reduce((s, it) => s + (Number(it.itemTotalCommission) || 0), 0);
    const checked = selectedIds.includes(lo.id);
    return (
      <Card key={lo.id} className={cx('p-3.5', checked && 'border-gray-900 ring-1 ring-gray-900')}>
        <label className="flex cursor-pointer items-start gap-3">
          <input type="checkbox" className="mt-1 h-5 w-5 flex-shrink-0 rounded" checked={checked} onChange={() => toggleSelect(lo.id)} />
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <p className="truncate font-mono text-sm font-semibold text-gray-900">{lo.order_sn}</p>
              {matched ? <Badge tone="green">Tìm thấy</Badge> : <Badge tone="red">Không thấy</Badge>}
            </div>
            <p className="truncate text-xs text-gray-500">Cho: {lo.user?.email || '—'}</p>
          </div>
        </label>

        {matched ? (
          <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
            <div className="divide-y divide-gray-100 rounded-xl bg-gray-50 px-3">
              <InfoRow label="Shop">{sd.shopName || sd.shop_name || '—'}</InfoRow>
              <InfoRow label="Trạng thái">{sd.orderStatus || '—'}</InfoRow>
              <InfoRow label="Đặt hàng">{formatDateTime(fromUnix(sd.purchaseTime || sd.pending_date))}</InfoRow>
              <InfoRow label="HH Shopee">{formatCurrency(commission)}</InfoRow>
            </div>
            {items.slice(0, 3).map((it, idx) => (
              <div key={idx} className="flex items-center gap-2 text-xs">
                {(it.imageUrl || it.img_code) && (
                  <img src={it.imageUrl || it.img_code} alt="" className="h-8 w-8 flex-shrink-0 rounded-lg object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                )}
                <span className="min-w-0 flex-1 truncate text-gray-700">{it.itemName || it.item_name || '—'}</span>
                {isFraud(it) && <ShieldAlert className="h-4 w-4 flex-shrink-0 text-red-500" title="Gian lận" />}
                <span className="flex-shrink-0 text-gray-500">{formatDate(fromUnix(it.completeTime || it.complete_time))}</span>
              </div>
            ))}
            {items.length > 3 && <p className="text-xs text-gray-400">+{items.length - 3} sản phẩm khác</p>}
          </div>
        ) : (
          <p className="mt-2 text-xs text-red-600">{entry.reason || 'Không tìm thấy trong 30 ngày gần nhất'}</p>
        )}
      </Card>
    );
  };

  return (
    <div>
      <PageHeader
        title="Đơn thất lạc"
        subtitle="Yêu cầu tìm đơn từ người dùng; tra cứu Shopee 30 ngày gần nhất"
        actions={
          status === 'PENDING' && pendingRows.length > 0 ? (
            <Button variant="soft" onClick={() => lookup(pendingRows)} loading={searching}>
              <ScanSearch className="h-4 w-4" /> Tra cứu {pendingRows.length} đơn trang này
            </Button>
          ) : null
        }
      />

      <div className="filter-bar">
        <SearchInput className="lg:min-w-[18rem] lg:flex-1" value={search} onChange={(v) => { setSearch(v); setPage(1); }} placeholder="Tìm theo mã đơn" />
        <Segmented
          value={status}
          onChange={(v) => { setStatus(v); setPage(1); }}
          options={[
            { value: 'PENDING', label: 'Chờ xử lý' },
            { value: 'SUCCESS', label: 'Thành công' },
            { value: 'FAILED', label: 'Từ chối' },
            { value: '', label: 'Tất cả' }
          ]}
        />
        <Segmented
          value={type}
          onChange={(v) => { setType(v); setPage(1); }}
          options={[
            { value: '', label: 'Mọi loại' },
            { value: '0', label: 'Mất đơn' },
            { value: '1', label: 'Ghi nhận gián tiếp' }
          ]}
        />
        <Segmented
          value={platform}
          onChange={(v) => { setPlatform(v); setPage(1); }}
          options={[
            { value: '', label: 'Mọi sàn' },
            { value: 'shopee', label: 'Shopee' },
            { value: 'tiktokshop', label: 'TikTok Shop' },
            { value: 'lazada', label: 'Lazada' }
          ]}
        />
      </div>

      <ErrorBanner message={error ? 'Lỗi khi tải danh sách đơn thất lạc' : ''} onRetry={refetch} />

      {isLoading ? (
        <SkeletonList />
      ) : (
        <ResponsiveList
          items={rows}
          renderCard={renderRow}
          columns={columns}
          empty={<EmptyState icon={SearchX} title="Không có yêu cầu nào" />}
        />
      )}

      {pagination.total > 0 && (
        <Pagination
          page={page}
          totalPages={pagination.pages}
          total={pagination.total}
          onChange={setPage}
          limit={limit}
          onLimitChange={(n) => { setLimit(n); setPage(1); }}
          disabled={isFetching}
        />
      )}

      {/* Kết quả tra cứu */}
      <Sheet
        open={!!results}
        onClose={() => !busy && setResults(null)}
        size="lg"
        title={results ? `Kết quả tra cứu · ${results.filter((r) => r.matched).length}/${results.length} tìm thấy` : ''}
        footer={results && (
          <div className="space-y-2">
            <p className="text-xs text-gray-500">Đã chọn {selectedEntries.length} yêu cầu ({selectedMatched.length} tìm thấy)</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="text-red-600" onClick={() => openReject(selectedEntries.map((r) => r.lostOrder.id))} disabled={busy || selectedEntries.length === 0}>
                <XIcon className="h-4 w-4" /> Từ chối ({selectedEntries.length})
              </Button>
              <Button variant="success" onClick={() => createOrders(selectedMatched)} loading={busy} disabled={selectedMatched.length === 0}>
                <Check className="h-4 w-4" /> Tạo đơn ({selectedMatched.length})
              </Button>
            </div>
          </div>
        )}
      >
        {results && <div className="grid grid-cols-1 gap-3 md:grid-cols-2">{results.map(renderResult)}</div>}
      </Sheet>

      {/* Lý do từ chối */}
      <Sheet
        open={!!rejectTarget}
        onClose={() => !busy && setRejectTarget(null)}
        title={`Từ chối ${rejectTarget?.length || 0} yêu cầu`}
        footer={<Button variant="danger" className="w-full" onClick={confirmReject} loading={busy}>Xác nhận từ chối</Button>}
      >
        <p className="mb-2 text-sm text-gray-600">Lý do sẽ được gửi thông báo cho người dùng.</p>
        <div className="flex flex-wrap gap-2">
          {[...REJECT_REASONS, 'OTHER'].map((r) => (
            <button
              key={r}
              type="button"
              onClick={() => setRejectReason(r)}
              className={cx('rounded-full border px-3 py-2 text-sm', rejectReason === r ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 text-gray-600')}
            >
              {r === 'OTHER' ? 'Lý do khác' : r}
            </button>
          ))}
        </div>
        {rejectReason === 'OTHER' && (
          <input className={cx(inputClass, 'mt-3')} placeholder="Nhập lý do cụ thể" value={customReason} onChange={(e) => setCustomReason(e.target.value)} autoFocus />
        )}
      </Sheet>
    </div>
  );
};

export default LostOrdersPage;
